import reduce from 'lodash/reduce';
import uniqBy from 'lodash/uniqBy';
import includes from 'lodash/includes';
import truncate from 'lodash/truncate';
import { getSearchTrackEvent } from '../lib/analytics';

const filterKeyArr = {
  instrument: 'Instrument',
  type: 'Type',
  style: 'Genre',
  'artists.name': 'Artists',
};
const skillTypes = [
  'basics',
  'chord',
  'exercise',
  'glossary',
  'skill',
  'technique',
  'theory',
  'tone',
];
const guitarTypes = ['acoustic-guitar', 'electric-guitar'];
const generateFilterItem = (id, subTitle, title, url) => ({ id, subTitle, title, url });
const generateSummaryItem = (items, title, total, url) => ({ items, title, total, url });
const searchBasePath = '/search/results';

function clearFilters(filterObj = {}) {
  const filters = {};
  Object.keys(filterKeyArr).forEach(key => {
    filters[key] = null;
  });
  return { ...filters, ...filterObj };
}

function getFacetsTotal(facets, keyArr = []) {
  if (!facets) return 0;
  return reduce(
    facets,
    (sum, value, key) => {
      if (keyArr.includes(key)) {
        return sum + value;
      }
      return sum;
    },
    0,
  );
}

function getSummaryTitles(query = '', facets = {}) {
  const facetTypes = facets.type || {};
  const facetArtists = facets['artists.name'] || {};
  const skillTotal = getFacetsTotal(facetTypes, skillTypes);
  const artistsTotal = Object.keys(facetArtists).length;
  return {
    song: generateSummaryItem(
      [],
      'Songs',
      facetTypes.song || 0,
      `${searchBasePath}?query=${query}&type=song`,
    ),
    riff: generateSummaryItem(
      [],
      'Riffs',
      facetTypes.riff || 0,
      `${searchBasePath}?query=${query}&type=riff`,
    ),
    artists: generateSummaryItem([], 'Artists', artistsTotal, null),
    skill: generateSummaryItem(
      [],
      'Skills',
      skillTotal || 0,
      `${searchBasePath}?query=${query}&type=skill`,
    ),
  };
}

function getArtistSummaryItems(hit = {}, artists = []) {
  const artist = artists[0] || { name: '', image: '' };
  const { image, name } = artist;
  return {
    ...hit,
    artists: null,
    image,
    level: null,
    title: name,
    type: 'artist',
    url: `${searchBasePath}?query=${name}&artist=${name}`,
  };
}

function getFieldStyles(pill, small) {
  const smallStyle = small ? '' : 'pa3-l f3-l';
  const inputNormal = `ba br-0 b--moon-gray br2 br--left pa2 f6 source-sans ${smallStyle} w-100`;
  const inputPill = 'ba br-pill b--mid-gray outline-0 pa2 pl4 bg-transparent near-white w-100';
  const inputStyle = `${pill ? inputPill : inputNormal}`;
  const buttonNormal = `flex items-center bn outline-0 pa2 f4 tc bg-blue justify-center pointer ${smallStyle}`;
  const buttonPill = `absolute outline-0 top-0 left-0 h-100 flex items-center f5 pl3 pa0 ma0 bg-transparent bn mid-gray pointer`;
  const buttonStyle = `${pill ? buttonPill : buttonNormal}`;
  return { inputStyle, buttonStyle };
}

function parseUserDataObject(dataObj, query) {
  const hit = {
    ...dataObj,
    slug: dataObj.url,
    type: 'custom-search',
  };
  return {
    ...dataObj,
    trackEvent: getSearchTrackEvent(hit, query),
  };
}

function parseHitOverlay(type, title = '') {
  return {
    overlayColor: type === 'chord' ? 'blue' : '',
    overlayTitle: type === 'chord' ? title.split(' ')[0] : '',
  };
}

function addHitToCategory(summaryTitles, hit, category) {
  if (!summaryTitles[category]) {
    // eslint-disable-next-line no-param-reassign
    summaryTitles[category] = generateSummaryItem([], category, 0);
  }
  summaryTitles[category].items.push(hit);
}

function addHitToSummary(summaryTitles, hit) {
  const { artists, category, title, type } = hit;
  if (artists) {
    summaryTitles.artists.items.push(getArtistSummaryItems(hit, artists));
  }
  if (category) {
    return addHitToCategory(summaryTitles, hit, category);
  }
  if (type === 'song' || type === 'riff') {
    return summaryTitles[type].items.push({ ...hit, level: null });
  }
  return summaryTitles.skill.items.push({
    ...hit,
    ...parseHitOverlay(type, title),
    type,
  });
}

function formatSummary(hits, facets, query) {
  if (!hits) return [];
  const summaryTitles = getSummaryTitles(query, facets);
  hits.forEach((hit, index) => {
    const parsedHit = { ...hit, trackEvent: getSearchTrackEvent(hit, query, index) };
    addHitToSummary(summaryTitles, parsedHit);
  });
  summaryTitles.artists.items = uniqBy(summaryTitles.artists.items, 'title');
  return Object.keys(summaryTitles).map(key => {
    const summary = summaryTitles[key];
    return { ...summary, items: summary.items.slice(0, 6) };
  });
}

function getParsedArtist(hit) {
  const { _highlightResult } = hit;
  const { artists } = _highlightResult;
  if (!Array.isArray(artists)) {
    if (process.env.ENVIRONMENT !== 'prod') {
      // eslint-disable-next-line
      console.warn('artist field is missing in search hit', hit);
    }
    return null;
  }

  if (artists.length > 0) {
    return artists[0].name.value;
  }

  return null;
}

function getTableItemTitle(hit = {}) {
  const { artists, objectType, type } = hit;
  if (type === 'artist') return '';
  if (type === 'song' || type === 'riff') {
    const artist = artists ? artists[0] : { name: '' };
    const artistName = getParsedArtist(hit) || truncate(artist.name);
    return artistName;
  }
  return objectType === 'course' ? 'Course' : type;
}

function getTableItemType(type) {
  if (type !== 'artist' && type !== 'song' && type !== 'riff') {
    return 'Skill';
  }
  return type;
}

function getParsedTitle(hit) {
  // eslint-disable-next-line no-underscore-dangle
  return hit._highlightResult ? hit._highlightResult.title.value : hit.title;
}

function formatTableData(hits, query = '', hasSubscription) {
  if (!hits || !hits.length) return [];
  const lessonPrefix = hasSubscription ? 'lesson' : 'lessons';
  const coursePrefix = hasSubscription ? 'course' : 'courses';
  return hits.map((hit, index) => {
    const { objectType, slug, url } = hit;
    const curriculumURL = objectType === 'lesson' ? lessonPrefix : coursePrefix;
    const itemUrl = url || `/${curriculumURL}/${slug}`;
    const trackEvent = getSearchTrackEvent(hit, query, index);

    return {
      ...hit,
      trackEvent,
      url: itemUrl,
      title: getParsedTitle(hit),
      isSearch: true,
      items: [
        {
          ...hit,
          title: getTableItemTitle(hit),
          trackEvent,
          type: getTableItemType(hit.type),
          url: itemUrl,
        },
      ],
    };
  });
}

function getInstrumentFacetFilter(facetInstruments, query, current) {
  if (!facetInstruments) return null;
  const instrumentObj = {
    guitar: generateFilterItem(
      'guitar',
      getFacetsTotal(facetInstruments, guitarTypes) || 0,
      'Guitar',
      `${searchBasePath}?query=${query}&instrument=guitar`,
    ),
    ukulele: generateFilterItem(
      'ukulele',
      facetInstruments.ukulele || 0,
      'Ukulele',
      `${searchBasePath}?query=${query}&instrument=ukulele`,
    ),
  };
  instrumentObj.bass = generateFilterItem(
    'bass',
    facetInstruments.bass || 0,
    'Bass',
    `${searchBasePath}?query=${query}&instrument=bass`,
  );
  return {
    current,
    label: filterKeyArr.instrument,
    items: Object.keys(instrumentObj).map(key => instrumentObj[key]),
  };
}

function getTypeFacetFilter(facetTypes, query, current, instrument = 'guitar') {
  const typeObj = {
    song: generateFilterItem(
      'song',
      facetTypes.song || 0,
      'Songs',
      `${searchBasePath}?query=${query}&type=song&instrument=${instrument}`,
    ),
    riff: generateFilterItem(
      'riff',
      facetTypes.riff || 0,
      'Riffs',
      `${searchBasePath}?query=${query}&type=riff&instrument=${instrument}`,
    ),
    skill: generateFilterItem(
      'skill',
      getFacetsTotal(facetTypes, skillTypes),
      'Skills',
      `${searchBasePath}?query=${query}&type=skill&instrument=${instrument}`,
    ),
  };
  const mapped = Object.keys(typeObj).map(key => typeObj[key]);
  const filtered = mapped.filter(item => item.subTitle !== 0);
  return { current, label: filterKeyArr.type, items: filtered };
}

function getFacetFilterItems(facets, key, query, instrument = 'guitar') {
  const facetKeys = Object.keys(facets[key]);
  const facetLength = facetKeys.length;
  let items = facetKeys.map(facetLabel =>
    generateFilterItem(
      facetLabel,
      facets[key][facetLabel],
      facetLabel,
      `${searchBasePath}?query=${query}&instrument=${instrument}&${
        key === 'artists.name' ? 'artist' : key
      }=${facetLabel}`,
    ),
  );
  if (facetLength > 10) {
    items = items.slice(0, 10);
  }
  return items;
}

function facetsIntoFilterArr(facets, query, currentObj) {
  if (!facets) return [];
  const filterArr = [
    getInstrumentFacetFilter(facets.instrument || {}, query, currentObj.instrument || ''),
  ];
  if (facets.type) {
    filterArr.push(
      getTypeFacetFilter(facets.type || {}, query, currentObj.type, currentObj.instrument),
    );
  }
  Object.keys(filterKeyArr).forEach(key => {
    if (facets[key] && key !== 'instrument' && key !== 'type') {
      const label = filterKeyArr[key];
      const items = getFacetFilterItems(facets, key, query, currentObj.instrument);
      filterArr.push({
        current: currentObj[unescape(key)] || null,
        label,
        items,
      });
    }
  });
  return filterArr;
}

function parseFacets(filterFacets, totalFacets, filterObj = {}) {
  const parsed = {};
  Object.keys(totalFacets).forEach(facetKey => {
    parsed[facetKey] = filterObj[facetKey] ? totalFacets[facetKey] : filterFacets[facetKey];
  });
  return parsed;
}

function parseSearchFilter(filterObj) {
  if (!filterObj) return '';
  const instrument =
    filterObj.instrument === 'guitar'
      ? '(instrument:electric-guitar OR instrument:acoustic-guitar)'
      : `instrument:${filterObj.instrument}`;
  const stringArr = [instrument];
  Object.keys(filterObj).forEach(key => {
    const value = filterObj[key];
    if (key === 'type' && value === 'skill') {
      const lessonSkills = skillTypes.map(skill => `type:${skill}`);
      const skillFilterStr = lessonSkills.join(' OR ');
      stringArr.push(` AND (${skillFilterStr})`);
    } else if (value && key !== 'instrument') {
      stringArr.push(` AND ${key}:"${value}"`);
    }
  });
  return stringArr.join('');
}

function isCustomCategory(title = '') {
  const titles = getSummaryTitles();
  const titlesArr = Object.keys(titles).map(key => titles[key].title);
  return !includes(titlesArr, title);
}

export {
  clearFilters,
  filterKeyArr,
  facetsIntoFilterArr,
  formatSummary,
  formatTableData,
  generateFilterItem,
  getArtistSummaryItems,
  getFacetsTotal,
  getFieldStyles,
  getInstrumentFacetFilter,
  getTypeFacetFilter,
  getSummaryTitles,
  guitarTypes,
  isCustomCategory,
  parseFacets,
  parseSearchFilter,
  parseUserDataObject,
  searchBasePath,
  skillTypes,
};
